import { normalize } from 'path-browserify';
import { Route, RouteBuilder, RouteBuilderOptions } from './route.types';

class DefaultRouteBuilder implements RouteBuilder {
  constructor(private defaultOptions: RouteBuilderOptions) {}
  public getAdminRoute(options?: RouteBuilderOptions): string {
    return this.build(Route.ADMIN, options);
  }

  public getAdminTenantRoute(options?: RouteBuilderOptions): string {
    return this.build(Route.ADMIN_TENANT, options);
  }

  public getAdminRoleRoute(
    roleId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.ADMIN_ROLES}/${roleId}`, options);
  }

  public getAdminRolesRoute(options?: RouteBuilderOptions): string {
    return this.build(Route.ADMIN_ROLES, options);
  }

  public getAdminUsersRoute(options?: RouteBuilderOptions): string {
    return this.build(Route.ADMIN_USERS, options);
  }

  public getAdminEventIntegrationsRoute(options?: RouteBuilderOptions): string {
    return this.build(Route.ADMIN_INTEGRATIONS, options);
  }

  public getAdminUserRoute(
    userId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.ADMIN_USERS}/${userId}`, options);
  }

  public getAdminMachinesRoute(options?: RouteBuilderOptions): string {
    return this.build(`${Route.ADMIN_MACHINES}`, options);
  }

  public getAdminTeamsRoute(options?: RouteBuilderOptions): string {
    return this.build(`${Route.ADMIN_TEAMS}`, options);
  }

  public getAdminExportRoute(options?: RouteBuilderOptions): string {
    return this.build(`${Route.ADMIN_EXPORT}`, options);
  }

  public getAppModeSelectionRoute(): string {
    return this.build(Route.APP_MODE, {
      prefix: '',
    });
  }

  public getAuthSignupRoute(): string {
    return this.build(Route.AUTH_SIGNUP, {
      prefix: '',
    });
  }

  public getAuthLoginRoute(): string {
    return this.build(Route.AUTH_LOGIN, {
      prefix: '',
    });
  }

  public getAuthLogoutRoute(): string {
    return this.build(Route.AUTH_LOGOUT, {
      prefix: '',
    });
  }

  public getAuthConfirmRoute(): string {
    return this.build(Route.AUTH_CONFIRM, {
      prefix: '',
    });
  }

  public getAuthConfirmPasswordRoute(): string {
    return this.build(Route.AUTH_CONFIRM_PASSWORD, {
      prefix: '',
    });
  }

  public getBillingRoute(options?: RouteBuilderOptions): string {
    return this.build(`${Route.BILLING}`, options);
  }

  public getDirectConnectKioskRoute(): string {
    return this.build(`${Route.DIRECT_CONNECT}/kiosk`, {
      prefix: '',
    });
  }

  public getDirectConnectMachineRoute(machineId: string): string {
    return this.build(`${Route.DIRECT_CONNECT}/machine/${machineId}`, {
      prefix: '',
    });
  }

  public getDirectConnectSelectMachineRoute(): string {
    return this.build(`${Route.DIRECT_CONNECT}/select-machine`, {
      prefix: '',
    });
  }

  public getHomeRoute(options?: RouteBuilderOptions): string {
    return this.build(Route.HOME, options);
  }

  public getMachinesRoute(
    teamId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.TEAMS}/${teamId}/${Route.MACHINES}`, options);
  }

  public getMachineRoute(
    teamId: string,
    machineId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}`,
      options
    );
  }

  public getMachineApplicationsRoute(
    teamId: string,
    machineId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/applications`,
      options
    );
  }

  public getMachineApplicationRoute(
    teamId: string,
    machineId: string,
    applicationId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/applications/${applicationId}`,
      options
    );
  }

  public getMachineDetailsRoute(
    teamId: string,
    machineId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/details`,
      options
    );
  }

  public getMachineMaintenanceRoute(
    teamId: string,
    machineId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/maintenance`,
      options
    );
  }

  public getMachineEventDefinitionsRoute(
    teamId: string,
    machineId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/events`,
      options
    );
  }

  public getMachineEventDefinitionRoute(
    teamId: string,
    machineId: string,
    eventDefinitionId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/events/${eventDefinitionId}`,
      options
    );
  }

  public getMachineFileRoute(
    teamId: string,
    machineId: string,
    fileId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/files/${fileId}`,
      options
    );
  }

  public getMachineFilesRoute(
    teamId: string,
    machineId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/files`,
      options
    );
  }

  public getMachineIORoute(
    teamId: string,
    machineId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/io`,
      options
    );
  }

  public getMachineLogsRoute(
    teamId: string,
    machineId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/logs`,
      options
    );
  }

  public getMachineRealtimeRoute(
    teamId: string,
    machineId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/realtime`,
      options
    );
  }

  public getMachineScratchpadRoute(
    teamId: string,
    machineId: string,
    scratchpadId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/scratchpad/${scratchpadId}`,
      options
    );
  }

  public getMachineVariablesRoute(
    teamId: string,
    machineId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(
      `${Route.TEAMS}/${teamId}/${Route.MACHINES}/${machineId}/variables`,
      options
    );
  }

  public getNotificationsRoute(options?: RouteBuilderOptions): string {
    return this.build(Route.NOTIFICATIONS, options);
  }

  public getTeamsRoute(options?: RouteBuilderOptions): string {
    return this.build(Route.TEAMS, options);
  }

  public getTeamRoute(teamId: string, options?: RouteBuilderOptions): string {
    return this.build(`${Route.TEAMS}/${teamId}`, options);
  }

  public getTeamChartRoute(
    teamId: string,
    chartId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.TEAMS}/${teamId}/chart/${chartId}`, options);
  }

  public getTeamChartTemplatesRoute(
    teamId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.TEAMS}/${teamId}/chart-templates`, options);
  }

  public getTeamChatRoute(
    teamId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.TEAMS}/${teamId}/chat`, options);
  }

  public getTeamChatThreadRoute(
    teamId: string,
    threadId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.TEAMS}/${teamId}/chat/${threadId}`, options);
  }

  public getTeamHomeRoute(
    teamId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.TEAMS}/${teamId}/home`, options);
  }

  public getTeamMoreRoute(
    teamId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.TEAMS}/${teamId}/more`, options);
  }

  public getTeamSetupRoute(
    teamId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.TEAMS}/${teamId}/setup`, options);
  }

  public getTeamUsersRoute(
    teamId: string,
    options?: RouteBuilderOptions
  ): string {
    return this.build(`${Route.TEAMS}/${teamId}/users`, options);
  }

  public getNewTeamRoute(options?: RouteBuilderOptions): string {
    return this.build(Route.NEW_TEAM, options);
  }

  public getUsersRoute(options?: RouteBuilderOptions): string {
    return this.build('users', options);
  }

  public getUserRoute(userId: string, options?: RouteBuilderOptions): string {
    return this.build(`${Route.USER}/${userId}`, options);
  }

  public build(path: string, options?: RouteBuilderOptions): string {
    const merged = {
      ...this.defaultOptions,
      ...options,
    };

    const { prefix, queryParams } = merged;

    let result = path.startsWith('/') ? path : `/${path}`;

    if (prefix) {
      const modified = path.startsWith('/') ? path.substring(1) : path;
      result = `${prefix}/${modified}`;
    }

    if (queryParams) {
      let paramStr: string | undefined;

      if (typeof queryParams === 'string') {
        paramStr = queryParams;
      } else {
        paramStr = new URLSearchParams(queryParams).toString();
      }

      if (paramStr.startsWith('?')) {
        result += paramStr;
      } else {
        result += `?${paramStr}`;
      }
    }

    return normalize(result);
  }
}

export { DefaultRouteBuilder };
